import { Store } from 'redux';
import { IAuthenticationContext, createContext, EEventType, ESentEmailType } from '@cian/authentication-widget';

import { IApplicationContext } from '../../../types/applicationContext';
import { IApplicationState } from '../../../types/redux';

let authContextProvider: AuthContextProvider | null = null;

class AuthContextProvider {
  private readonly applicationContext: IApplicationContext;
  private authContext: IAuthenticationContext | null;
  private store: Store<IApplicationState>;

  private get subdomain() {
    return this.store.getState().page.subdomain;
  }

  private get returnUrl() {
    return this.store.getState().page.returnUrl;
  }

  public constructor(applicationContext: IApplicationContext, store: Store<IApplicationState>) {
    this.applicationContext = applicationContext;
    this.store = store;
  }

  public get context() {
    return this.authContext;
  }

  public init = async () => {
    if (this.authContext) {
      return;
    }

    this.initContext();
  };

  private initContext = () => {
    const { config, httpApi, logger } = this.applicationContext;
    this.authContext = createContext({
      httpApi,
      logger,
      config,
      subdomain: this.subdomain ?? 'www',
    });

    this.authContext.setRegistrationParams({ isProfessionalUser: true });

    this.authContext.on(EEventType.AuthenticateSucceed, this.authenticationSucceed.bind(this));
    this.authContext.on(EEventType.SocialAuthenticateSucceed, this.handleSocialAuthenticateSucceed.bind(this));
    this.authContext.on(EEventType.RegisterSucceed, this.handleRegisterSucceed.bind(this));
    this.authContext.on(EEventType.EmailSentClosed, this.handleEmailSentClosed.bind(this));
  };

  private handleRegisterSucceed = (event?: { senderType: string }) => {
    if (event && event.senderType) {
      this.authenticationSucceed();
    }
  };

  private handleEmailSentClosed = ({ type }: { type: ESentEmailType }) => {
    if (type === ESentEmailType.Registration) {
      this.authenticationSucceed();
    }
  };

  private handleSocialAuthenticateSucceed = ({ isNewUser }: { isNewUser: boolean }) => {
    if (!isNewUser) {
      this.authenticationSucceed();
    }
  };

  private authenticationSucceed() {
    if (!this.returnUrl) {
      return;
    }

    window.location.replace(this.returnUrl);
  }
}

export function init(context: IApplicationContext, store: Store) {
  if (!authContextProvider) {
    authContextProvider = new AuthContextProvider(context, store);
  }

  return authContextProvider;
}
